import { useRef, useState } from "react";
import { projectData, roundSqFt } from "@domain/project/projectData";
import { planBSqFt, planBSpaces } from "@domain/budget/planB";
import StatusBadge from "@ui/StatusBadge";
import InfoBentoCard from "@ui/InfoBentoCard";
import ProgressDonut from "@ui/ProgressDonut";
import ImageViewer from "@ui/ImageViewer";
import useSpaceGallery from "@features/dashboard/hooks/useSpaceGallery";
import useHeroImage from "@features/dashboard/hooks/useHeroImage";
import useInvoices from "@features/invoices/hooks/useInvoices";
import ContractsInProgress from "@features/dashboard/components/ContractsInProgress";
import { useProjectReview } from "@features/dashboard/hooks/useProjectReview";
import { useLatestAIReview } from "@features/chat/hooks/useLatestAIReview";
import { useChatUI } from "@contexts/ChatContext";
import { Ruler, Calendar, DollarSign, Plus, Trash2, Sparkles, Loader2, RefreshCw } from "lucide-react";

function formatDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Dashboard({ phases, budget }) {
  const { images, addImage, removeImage } = useSpaceGallery();
  const { heroImage } = useHeroImage();
  const { items: invoices } = useInvoices();
  const projectReview = useProjectReview();
  const { observationsFor, generalObservations, observations: allObservations, review } = useLatestAIReview();
  const { seedAndOpen } = useChatUI();
  const fileInputRef = useRef(null);
  const [viewerIndex, setViewerIndex] = useState(null);

  // Observations bucketed by which Dashboard container they belong to
  const budgetObservations = allObservations.filter((o) =>
    (o.citations ?? []).some((c) => c.type === "budget")
  );
  const phaseObservations = allObservations.filter((o) =>
    (o.citations ?? []).some((c) => c.type === "phase")
  );
  const spaceObservations = allObservations.filter((o) =>
    (o.citations ?? []).some((c) => c.type === "floorplan") ||
    (o.citations ?? []).some(
      (c) => c.type === "general" && /sqft|space|area|room|layout/i.test(c.label || "")
    )
  );

  const selectContainer = (label, hint = "") => () => {
    seedAndOpen({
      topic: {
        title: `Discussing ${label}`,
        body: hint ? `${hint}` : `Ask any question about ${label}.`,
        containerLabel: label,
      },
    });
  };

  const totalTasks = phases.reduce((sum, p) => sum + p.tasks.length, 0);
  const completedTasks = phases.reduce(
    (sum, p) => sum + p.tasks.filter((t) => t.completed).length,
    0
  );
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const totalEstimated = budget.reduce((sum, c) => sum + c.estimated, 0);
  const totalActual = budget.reduce((sum, c) => sum + c.actual, 0);
  const budgetRemaining = totalEstimated - totalActual;

  const totalAreaSqFt = roundSqFt(projectData.currentSqFt + planBSqFt);
  const displayActiveSqFt = roundSqFt(planBSqFt);
  const displayCurrentSqFt = roundSqFt(projectData.currentSqFt);

  const costPerSqFt = planBSqFt > 0
    ? Math.round(totalEstimated / planBSqFt)
    : 0;
  const actualPerSqFt = planBSqFt > 0
    ? Math.round(totalActual / planBSqFt)
    : 0;

  const currentPhase = phases.find((p) => p.status === "in-progress") || phases[0];

  // Map budget category IDs to phase IDs
  const phaseBudgetMap = {
    1: [1, 27],          // Pre-Application: Arch & Eng, Design Review
    2: [2, 3, 28],       // Permits: Permits, Geotech, Arborist
    3: [4],              // Demolition
    4: [5, 6],           // Foundation & Framing
    5: [7, 8, 9],        // Rough-In: Electrical, Plumbing, HVAC
    6: [10],             // Insulation & Drywall
    7: [11, 12, 16, 17, 18, 20, 13, 14, 23, 24, 25, 26], // Interior Finishes + windows/doors/stucco
    8: [22],             // Final: Roof
  };

  const phaseCosts = {};
  phases.forEach((phase) => {
    const catIds = phaseBudgetMap[phase.id] || [];
    const est = budget.filter((c) => catIds.includes(c.id)).reduce((s, c) => s + c.estimated, 0);
    const act = budget.filter((c) => catIds.includes(c.id)).reduce((s, c) => s + c.actual, 0);
    phaseCosts[phase.id] = { estimated: est, actual: act };
  });

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await addImage(file);
      e.target.value = "";
    }
  };

  return (
    <div>
      {/* ── Full-bleed Hero ── */}
      <div className="relative w-full overflow-hidden" style={{ minHeight: "260px" }}>
        {/* Background: hero image → first gallery photo → gradient */}
        {heroImage?.url || images[0]?.imageUrl ? (
          <img
            src={heroImage?.url ?? images[0].imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 45%, #312e81 100%)" }}
          />
        )}
        {/* Scrim — heavier at bottom for text legibility */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.12) 100%)" }}
        />

        {/* Top-left: review button */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-3 flex-wrap">
          <button
            onClick={review ? projectReview.forceRefresh : projectReview.run}
            disabled={projectReview.loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-indigo-600/90 hover:bg-indigo-500 disabled:opacity-50 transition-colors backdrop-blur-sm"
          >
            {projectReview.loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : review ? (
              <RefreshCw className="w-3.5 h-3.5" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {projectReview.loading ? "Reviewing…" : review ? "Re-review my project" : "Review my project"}
          </button>
          {review && (
            <span className="text-[10px] text-white/50">
              Tap <span className="font-semibold text-white/70">!</span> badges to read observations
            </span>
          )}
          {projectReview.error && (
            <span className="text-[10px] text-red-400">{projectReview.error}</span>
          )}
        </div>

        {/* Bottom: donut overlay + project name */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-16 md:px-6 md:pb-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <ProgressDonut phases={phases} size={84} strokeWidth={9} onDark />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg md:text-2xl font-bold text-white leading-tight mb-0.5">
                {projectData.projectName}
              </h1>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
                {projectData.address}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Rest of content ── */}
      <div className="p-4 md:p-6 max-w-4xl mx-auto">

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <InfoBentoCard
          cornerGlow
          frostedGradient
          icon={<Ruler className="w-4 h-4" />}
          accent="green"
          title="Total Area"
          subtitle={`${displayCurrentSqFt} existing + ${displayActiveSqFt} new`}
          className="h-full"
        >
          <p className="text-lg font-bold text-emerald-400 mt-1">{totalAreaSqFt} ft²</p>
        </InfoBentoCard>

        <InfoBentoCard
          cornerGlow
          frostedGradient
          icon={<DollarSign className="w-4 h-4" />}
          accent="amber"
          title="Budget"
          className="h-full"
          aiObservations={budgetObservations}
          aiContainerLabel="the project budget"
          onClick={selectContainer("the project budget", "What should I be most concerned about?")}
        >
          <p className="text-lg font-bold text-amber-400 mt-1">${budgetRemaining.toLocaleString()}</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[10px] text-gray-500">remaining</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-mono">
              ${costPerSqFt}/ft²
            </span>
          </div>
        </InfoBentoCard>

        <InfoBentoCard
          cornerGlow
          frostedGradient
          icon={<DollarSign className="w-4 h-4" />}
          accent={budgetRemaining >= 0 ? "green" : "rose"}
          title="Actuals"
          subtitle={`${totalEstimated > 0 ? Math.round((totalActual / totalEstimated) * 100) : 0}% of budget spent`}
          className="h-full"
        >
          <p className={`text-lg font-bold mt-1 ${budgetRemaining >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            ${totalActual.toLocaleString()}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[10px] text-gray-500">spent</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${budgetRemaining >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
              ${actualPerSqFt}/ft²
            </span>
          </div>
        </InfoBentoCard>

        <InfoBentoCard
          cornerGlow
          frostedGradient
          icon={<Calendar className="w-4 h-4" />}
          accent="purple"
          title="Current Phase"
          subtitle={currentPhase ? `${formatDate(currentPhase.startDate)} → ${formatDate(currentPhase.endDate)}` : ""}
          className="h-full"
          aiObservations={
            currentPhase ? observationsFor("phase", currentPhase.id) : []
          }
          aiContainerLabel={`the ${currentPhase?.name ?? "current"} phase`}
          onClick={
            currentPhase
              ? selectContainer(
                  `the ${currentPhase.name} phase`,
                  "Is the scope/timeline realistic?"
                )
              : undefined
          }
        >
          <p className="text-xs font-semibold text-gray-200 mt-1">{currentPhase?.name || "—"}</p>
        </InfoBentoCard>
      </div>

      {/* Contracts in progress (Phase 2.5) — auto-hides when no contracts */}
      <ContractsInProgress budget={budget} invoices={invoices} />

      {/* Phase Overview */}
      <InfoBentoCard
        cornerGlow
        frostedGradient
        title="Phase Overview"
        accent="blue"
        className="mb-6"
        aiObservations={phaseObservations}
        aiContainerLabel="the project timeline"
        onClick={selectContainer(
          "the project timeline",
          "Which phases are most at risk?"
        )}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Legend</p>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: "#10b981" }} />
            <span className="text-[11px] text-gray-400">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: "#3b82f6" }} />
            <span className="text-[11px] text-gray-400">In Progress</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: "#ef4444" }} />
            <span className="text-[11px] text-gray-400">Blocked</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: "rgba(255,255,255,0.12)" }} />
            <span className="text-[11px] text-gray-400">Not Started</span>
          </div>
        </div>
        <div className="space-y-1.5">
          {phases.map((phase) => {
            const done = phase.tasks.filter((t) => t.completed).length;
            const total = phase.tasks.length;
            const phasePct = total > 0 ? Math.round((done / total) * 100) : 0;
            const costs = phaseCosts[phase.id] || { estimated: 0, actual: 0 };
            const spentPct = costs.estimated > 0 ? Math.min(Math.round((costs.actual / costs.estimated) * 100), 100) : 0;
            const overBudget = costs.actual > costs.estimated;
            return (
              <div
                key={phase.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-2 rounded-lg gap-2 md:gap-3"
                style={{ background: 'var(--bg-row-hover)' }}
              >
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <span className="text-xs text-gray-300 truncate flex-1 md:flex-none md:w-40">{phase.name}</span>
                  <StatusBadge status={phase.status} />
                </div>
                <div className="flex items-center gap-3 md:gap-4 md:flex-1 md:justify-end flex-wrap md:flex-nowrap">
                  {/* Cost meter */}
                  <div className="flex items-center gap-2 min-w-0 flex-1 md:flex-none">
                    <div className="flex-1 md:w-20 rounded-full h-1.5" style={{ background: 'var(--bg-progress-track)' }}>
                      <div
                        className={`h-1.5 rounded-full ${overBudget ? "bg-red-500/60" : spentPct > 75 ? "bg-amber-500/60" : "bg-emerald-500/40"}`}
                        style={{ width: `${spentPct}%` }}
                      />
                    </div>
                    <span className={`text-xs font-mono font-semibold text-right ${overBudget ? "text-red-400" : "text-gray-300"}`}>
                      ${costs.actual.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-600">/</span>
                    <span className="text-xs font-mono text-gray-400">
                      ${costs.estimated.toLocaleString()}
                    </span>
                  </div>
                  {/* Task progress */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-14 rounded-full h-1" style={{ background: 'var(--bg-progress-track)' }}>
                      <div
                        className="bg-blue-500/60 h-1 rounded-full"
                        style={{ width: `${phasePct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-300 font-mono font-semibold w-10 text-right">
                      {done}/{total}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </InfoBentoCard>

      {/* Space Breakdown */}
      <InfoBentoCard
        cornerGlow
        frostedGradient
        title="Space Breakdown"
        accent="cyan"
        aiObservations={spaceObservations}
        aiContainerLabel="the space layout"
        onClick={selectContainer(
          "the space layout",
          "Any concerns with the room sizes or flow?"
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {planBSpaces.map((space) => (
            <div
              key={space.name}
              className="p-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/20 text-center"
            >
              <p className="text-lg font-bold text-cyan-400">{space.sqFt} ft²</p>
              <p className="text-[10px] text-gray-400">{space.name}</p>
            </div>
          ))}
        </div>

        {/* Space Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setViewerIndex(idx);
              }}
              className="relative group rounded-lg overflow-hidden border border-cyan-500/20 text-left focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              aria-label="View image"
            >
              <img src={img.imageUrl} alt="" className="w-full h-32 object-cover" />
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(img);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    removeImage(img);
                  }
                }}
                className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-black/70 text-gray-200 hover:text-red-400 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Delete image"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </span>
            </button>
          ))}
          <button
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            className="flex flex-col items-center justify-center h-32 rounded-lg border border-dashed border-cyan-500/30 text-gray-500 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors"
          >
            <Plus className="w-5 h-5 mb-1" />
            <span className="text-[10px]">Add Image</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </InfoBentoCard>

      {viewerIndex !== null && images[viewerIndex] && (
        <ImageViewer
          items={images}
          startIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onDelete={(img) => removeImage(img)}
        />
      )}
      </div>{/* end max-w content */}
    </div>
  );
}
